import DoneIcon from '@mui/icons-material/Done';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8849 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8849 Schedule 6 Instructions | Claim HVUT Refund Online"
          description="Learn how to file Form 8849 Schedule 6 with TaxZerone to claim HVUT (Form 2290) refunds for sold, destroyed, stolen, or low-mileage vehicles."
          keywords=""
          canonical="resources/excise-tax/form-8849-schedule-6-instructions/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight"> Form 8849 Schedule 6</span>
          </span>{' '}
          Instructions: Other Claims
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Claim a Refund for Your Heavy Vehicle with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8849instrcution`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-8849-schedule-6-instructions/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 8849 Schedule 6 at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Purpose:
                            </strong>{' '}
                            Schedule 6 (Other Claims) is used to request{' '}
                            <strong style={{ fontWeight: 600 }}>
                              excise tax refunds
                            </strong>
                            , including eligible Heavy Vehicle Use Tax (HVUT)
                            refunds.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              When to File:
                            </strong>{' '}
                            Use Schedule 6 if a vehicle was{' '}
                            <strong style={{ fontWeight: 600 }}>
                              sold, destroyed, stolen, driven under the mileage
                              limit,
                            </strong>{' '}
                            or if HVUT was overpaid.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Required Information:
                            </strong>{' '}
                            Provide details such as{' '}
                            <strong style={{ fontWeight: 600 }}>
                              EIN, VIN, taxable weight, tax period, refund
                              reason, and supporting documents.
                            </strong>
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Filing Deadline:
                            </strong>{' '}
                            Submit your claim within{' '}
                            <strong style={{ fontWeight: 600 }}>3 years</strong>{' '}
                            from the original Form 2290 filing date or{' '}
                            <strong style={{ fontWeight: 600 }}>2 years</strong>{' '}
                            from the tax payment date, whichever is later.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/excise-tax/efile-irs-form-8849-schedule-6-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form 8849 Schedule 6
                      </Link>{' '}
                      is used to request refunds of excise taxes that don&apos;t
                      fall under the more specific categories found on other
                      schedules. This schedule is commonly referred to when
                      filing{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        other claims
                      </span>{' '}
                      that don’t have a dedicated form or schedule within Form
                      8849, especially after{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 2290
                      </Link>{' '}
                      has already been filed.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                      Whether your vehicle was sold, destroyed, or stolen,
                      Schedule 6 helps you reclaim the tax you already paid.
                    </Typography>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 8849 Schedule 6?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whento" sx={{ color: '#0202ff' }}>
                          When to Use Schedule 6?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#require" sx={{ color: '#0202ff' }}>
                          Requirements and Documentation to claim a refund for
                          the Heavy Vehicles
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Filing Deadline
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Step-by-Step Instructions to Fill Out Form 8849
                          Schedule 6 in TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Form 8849 Schedule 6?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Schedule 6 also referred as{' '}
                  <span style={{ fontWeight: 600 }}>
                    &quot;Other Claims&quot;
                  </span>{' '}
                  is submitted with Form 8849 to request refunds for:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>Overpaid or mistakenly paid federal excise taxes. </li>
                  <li>
                    HVUT paid for vehicles that were sold, destroyed, or
                    stolen.{' '}
                  </li>
                  <li>
                    Vehicles that were used less than 5,000 miles (or 7,500
                    miles for agricultural vehicles) during the tax year.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Unlike other schedules of Form 8849 that target specific taxes
                  (like fuel or retail taxes), Schedule 6 is designed for
                  miscellaneous or general refund situations.{' '}
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to claim your excise tax refund?
                      </Typography>
                      <Typography variant="body2">
                        With <span style={{ fontWeight: 600 }}>TaxZerone</span>{' '}
                        , filing{' '}
                        <span style={{ fontWeight: 600 }}>
                          Form 8849 with Schedule 6
                        </span>{' '}
                        is fast, simple, and stay IRS compliant. s
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 250, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8849instruction`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="whento"
                >
                  When to Use Schedule 6?
                </Typography>
                <Typography variant="body1">
                  You should file{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 8849 with Schedule 6
                  </span>{' '}
                  if:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Scenario
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Refund Eligibility
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Truck was sold, destroyed, or stolen{' '}
                          </TableCell>
                          <TableCell>
                            Refund for unused portion of HVUT
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Truck used less than 5,000 miles (or 7,500 for
                            agriculture){' '}
                          </TableCell>
                          <TableCell>Full HVUT refund </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>You overpaid HVUT on Form 2290 </TableCell>
                          <TableCell>Refund of excess tax paid </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="require"
                >
                  Required Information and Documentation to claim a Refund on
                  Heavy Vehicles
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Before you start{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-8849-schedule-6-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filling out Form 8849
                  </Link>
                  , make sure you have all the necessary information and
                  documents ready. Here’s what you’ll need:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>Your name and Address (individuals, businesses) </li>
                  <li>Employer Identification Number (EIN) </li>
                  <li>Vehicle Identification Number (VIN) </li>
                  <li>Taxable gross weight category </li>
                  <li>Date of Sale, Destroyed, or stolen </li>
                  <li>Refund amount </li>
                  <li>Name and Address of the purchaser of sold vehicle </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="filing"
                >
                  Filing Deadline
                </Typography>
                <Typography variant="body1">
                  Refund claims must be submitted:{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, listStyleType: 'none', '& li': { mb: 1.5 } }}
                >
                  <li>
                    <DoneIcon sx={{ mb: -0.8, color: '#1da94a' }} />
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Within 3 years
                    </span>{' '}
                    from the date you filed the original{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        2290 tax form
                      </Link>
                      ,
                    </span>{' '}
                    or{' '}
                  </li>
                  <li>
                    {' '}
                    <DoneIcon sx={{ mb: -0.8, color: '#1da94a' }} />
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Within 2 years
                    </span>{' '}
                    from the date, you paid the tax — whichever is later.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing within this period ensures the IRS processes your
                  refund without rejection.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Types of Claims Allowed on Schedule 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use <span style={{ fontWeight: 600 }}>Schedule 6</span> of
                  Form 8849 to claim a refund of Heavy Vehicle Use Tax (HVUT) if
                  a vehicle was{' '}
                  <span style={{ fontWeight: 600 }}>
                    sold, destroyed, stolen, Low Mileage Vehicles
                  </span>{' '}
                  or if you <span style={{ fontWeight: 600 }}>overpaid</span>.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  1. Sold:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    If you sold your truck during the tax year and already paid
                    HVUT, you can get a refund for the remaining months.{' '}
                  </li>
                  <li>
                    You’ll need the{' '}
                    <span style={{ fontWeight: 600 }}> date of sale, VIN</span>,
                    and just mention when you sold the vehicle. Uploading Bill
                    of sale is optional.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  2. Destroyed:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    If your vehicle was permanently damaged (e.g., fire or
                    accident), you may qualify for a partial refund.
                  </li>
                  <li>
                    Submit the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Tax period, FUM, VIN, Taxable gross weight, destruction
                      date
                    </span>
                    , and describe what happened and when it was destroyed.
                    Proof like an insurance report is optional.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  3. Stolen:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    If your vehicle was stolen and not recovered, you can claim
                    a prorated refund.
                  </li>
                  <li>
                    Enter the date it was stolen and explain the situation.
                    Uploading the theft document is optional.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  4. Low Mileage Vehicles:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    If your vehicle was driven{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      5,000 miles or less
                    </span>{' '}
                    (7,500 miles for agricultural vehicles) during the tax year,
                    you may qualify for a refund of the HVUT paid
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  5. Overpaid:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    Overpaid HVUT? This usually happens with{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      duplicate payments
                    </span>{' '}
                    or incorrect vehicle weight.
                  </li>
                  <li>Submit proof of both payments and an explanation.</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="step"
                >
                  Step-by-Step Instructions to Fill Out Form 8849 Schedule 6 in
                  TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here are the step-by –step instructions Form 8849 to fill out
                  a refund claim for excise tax{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 1: Sign In or Create an Account
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If you’re new to TaxZerone, click{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8849instructions`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Sign Up
                    </Link>{' '}
                    to create your account.{' '}
                  </li>
                  <li>
                    Already have an account? Just click{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signin/?ref=8849instructions`}
                      sx={{ color: '#0202ff' }}
                    >
                      Sign In
                    </Link>{' '}
                    to log in and get started.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 2: Start a New 8849 Filing
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  From the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Excise Tax Forms Dashboard
                  </span>
                  , click on{' '}
                  <span style={{ fontWeight: 600 }}>
                    &quot;Start New Return&quot;
                  </span>
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-start-img.webp" />
                  <source srcSet="/assets/images/8849-start-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-start-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: From the Excise Tax Forms Dashboard, click on Start New Return "
                    width={776}
                    height={370}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 3: Add or Select a Business
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-business-img.webp" />
                  <source srcSet="/assets/images/8849-business-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-business-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Add the new business or select from the address book to begin the filing."
                    width={760}
                    height={593}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> New users?</span> Click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;Add New Business&quot;
                    </span>{' '}
                    to enter your business details.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Returning users?
                    </span>{' '}
                    Click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;Select from Address Book&quot;
                    </span>{' '}
                    to use an existing business profile.
                  </li>
                </Box>
                <Typography variant="body1">
                  You&apos;ll need to enter:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Business Name</li>
                  <li> Employer Identification Number (EIN)</li>
                  <li>Business Address </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Make sure this matches what was used in your
                  original Form 2290 filing.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 4: Select Tax Year End Month
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-tax-year-img.webp" />
                  <source srcSet="/assets/images/8849-tax-year-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-tax-year-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Choose your tax year end month and click on “Save & Continue”."
                    width={776}
                    height={146}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Choose the{' '}
                    <span style={{ fontWeight: 600 }}>
                      tax year’s last month you&apos;re filing for
                    </span>
                    .{' '}
                  </li>
                  <li>
                    {' '}
                    Click{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      &quot;Save & Continue&quot;
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 5: Provide Refund Reason and Upload Documents
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-upload-img.webp" />
                  <source srcSet="/assets/images/8849-upload-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-upload-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Select the reason for the claim (e.g., vehicle was sold, destroyed, or stolen). Also, enter buyer details and if available, upload supporting documents."
                    width={762}
                    height={502}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Indicate the reason for the claim (e.g., vehicle was sold,
                    destroyed, or stolen) and,{' '}
                    <span style={{ fontWeight: 600 }}>if available</span>,
                    upload supporting documents.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 6: Add Low Mileage Vehicles (If Applicable)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-low-mileage-img.webp" />
                  <source srcSet="/assets/images/8849-low-mileage-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-low-mileage-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Enter low-mileage vehicle details and select “Save & Continue” or choose no low-mileage vehicle to report."
                    width={760}
                    height={370}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 4,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you&apos;re claiming a refund for{' '}
                    <span style={{ fontWeight: 600 }}>
                      low mileage vehicles
                    </span>
                    , enter the details and Click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;Save & Continue&quot;
                    </span>{' '}
                    or have no low mileage vehicles to report, click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;I don’t have low mileage vehicles to report&quot;
                    </span>
                    .
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 7: Report Overpayment of Tax (If Any)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-overpayment-img.webp" />
                  <source srcSet="/assets/images/8849-overpayment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-overpayment-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Enter Overpayment vehicles information such as VIN, Tax payment date, claim amount, explanation and proof documents."
                    width={760}
                    height={298}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    If you’ve{' '}
                    <span style={{ fontWeight: 600 }}>overpaid tax</span> for
                    any vehicle, enter the relevant details and Click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;Save & Continue&quot;
                    </span>{' '}
                  </li>
                  <li>
                    If not, simply click{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;I don’t have overpaid tax to report&quot;
                    </span>
                    .{' '}
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 8: Submit Your Filing
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8849-card-img.webp" />
                  <source srcSet="/assets/images/8849-card-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8849-card-img.png"
                    loading="lazy"
                    alt="Form 8849 Schedule 6: Enter the card details to make a payment and click on Pay & E-File to transmit your return to the IRS."
                    width={715}
                    height={584}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter card details to make payment and click the{' '}
                  <span style={{ fontWeight: 600 }}> Pay & E-File</span> button
                  to transmit your return to the IRS.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you choose{' '}
                  <span style={{ fontWeight: 600 }}>not to e-file</span> and
                  prefer to file by mail, you can send your completed Form 8849
                  to the following IRS address:
                </Alert>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                    Mailing Address:
                  </Typography>
                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      <span style={{ fontWeight: 600 }}>
                        Department of the Treasury
                      </span>
                      <br />
                      Internal Revenue Service
                      <br />
                      Cincinnati, OH 45999-0002
                    </Typography>
                  </Box>
                </Box>
                <Alert severity="info" icon={false} sx={{ mt: 3, mb: 4 }}>
                  <b>Tip:</b> However, e-filing through{' '}
                  <span style={{ fontWeight: 600 }}>TaxZerone</span> is faster,
                  safer, and provides real-time updates and status tracking.
                </Alert>

                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #ccc',
                    backgroundColor: '#e3f2fd',
                  }}
                  // className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Need a quick guide?
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Watch our easy step-by-step video on YouTube to learn
                        how to file Form 8849 and claim a refund on Heavy
                        Vehicle Use Tax (HVUT) for overpaid or exempt vehicles
                      </Typography>
                      <Typography variant="body2">
                        {' '}
                        ðŸŽ¥ <span style={{ fontWeight: 600 }}>
                          Watch here
                        </span>{' '}
                        [
                        <Link
                          href="https://www.youtube.com/watch?v=FtdBcvcfF_o&list=PLmvRKAW8mnOiHgODlf35zeTg7wBnQ1SZg&index=23"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Watch Tutorial Videos
                        </Link>
                        ]
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on IRS guidelines.
                        For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i8849s6"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions
                        </Link>{' '}
                        of Form 8849 Schedule 6.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Don’t Wait to Get Your HVUT Refund!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Make sure you’ve got your documents ready, choose Schedule 6 for
              your refund claim,{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              and double-check your details before filing.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8849instruction`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8849;
